import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type FormEvent } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { adminApi } from "../../api/admin";
import type { AdminLedgerRow } from "../../api/types";
import { formatCredits, formatDateTime, utcTooltip } from "../../shared/format";
import { Badge, Button, Card, DataTable, Input, Spinner } from "../../ui";
import { Pager, Select, useCursorPages } from "./shared";

export const LEDGER_KINDS = [
  "purchase",
  "debit",
  "reservation_settle",
  "grant",
  "adjustment",
  "refund",
  "dispute",
  "expiry",
  "deletion",
];

export const ledgerColumns: ColumnDef<AdminLedgerRow, unknown>[] = [
  {
    id: "created_at",
    header: "When",
    cell: ({ row }) => (
      <span title={utcTooltip(row.original.created_at)}>
        {formatDateTime(row.original.created_at)}
      </span>
    ),
  },
  { accessorKey: "product", header: "Product" },
  {
    id: "kind",
    header: "Kind",
    cell: ({ row }) => <Badge>{row.original.kind}</Badge>,
  },
  {
    id: "amount",
    header: "Amount",
    cell: ({ row }) => (
      <span
        className={row.original.amount < 0 ? "text-red-700" : "text-green-700"}
      >
        {formatCredits(row.original.amount)}
      </span>
    ),
  },
  {
    id: "balance_after",
    header: "Balance after",
    cell: ({ row }) => formatCredits(row.original.balance_after),
  },
  {
    id: "ref",
    header: "Ref",
    cell: ({ row }) =>
      row.original.ref_type
        ? `${row.original.ref_type}:${row.original.ref_id ?? ""}`
        : "—",
  },
  { accessorKey: "note", header: "Note" },
];

export function LedgerPage() {
  const { product } = useOutletContext<{ product: string | null }>();
  const [params] = useSearchParams();
  const initial = {
    org: params.get("org") ?? "",
    product: params.get("product") ?? "",
    kind: params.get("kind") ?? "",
    from: "",
    to: "",
  };
  const [draft, setDraft] = useState(initial);
  const [filters, setFilters] = useState(initial);
  const pages = useCursorPages();
  const query = useQuery({
    queryKey: ["admin", "ledger", filters, pages.cursor],
    queryFn: () =>
      adminApi.ledger({
        ...filters,
        product: product ?? filters.product,
        cursor: pages.cursor,
      }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFilters(draft);
    pages.reset();
  };
  return (
    <Card>
      <h1 className="mb-4 text-xl font-semibold">Ledger</h1>
      <form
        onSubmit={submit}
        className="mb-4 grid gap-3 md:grid-cols-4"
        aria-label="Filter ledger"
      >
        <Input
          label="Org id"
          value={draft.org}
          onChange={(e) => setDraft({ ...draft, org: e.target.value })}
        />
        <Input
          label="Product"
          value={draft.product}
          onChange={(e) => setDraft({ ...draft, product: e.target.value })}
        />
        <Select
          label="Kind"
          value={draft.kind}
          onChange={(kind) => setDraft({ ...draft, kind })}
          options={[
            { value: "", label: "any" },
            ...LEDGER_KINDS.map((k) => ({ value: k, label: k })),
          ]}
        />
        <Input
          label="From"
          type="date"
          value={draft.from}
          onChange={(e) => setDraft({ ...draft, from: e.target.value })}
        />
        <Input
          label="To"
          type="date"
          value={draft.to}
          onChange={(e) => setDraft({ ...draft, to: e.target.value })}
        />
        <div className="flex items-end">
          <Button type="submit">Filter</Button>
        </div>
      </form>
      {query.isLoading ? <Spinner /> : null}
      {query.data ? (
        <DataTable<AdminLedgerRow>
          columns={ledgerColumns}
          data={query.data.items}
          emptyMessage="No ledger entries match."
        />
      ) : null}
      <Pager pages={pages} nextCursor={query.data?.next_cursor} />
    </Card>
  );
}
